import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Send, MessageSquare, CheckCircle2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";

const FeedbackPage = () => {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) return alert("Please select a rating");

    setLoading(true);
    
    // Simulate API call or connect to your Supabase 'feedback' table
    const { error } = await supabase
      .from("feedback")
      .insert([{ rating, comment, created_at: new Date() }]);

    setLoading(false);
    if (!error) {
      setSubmitted(true);
      setTimeout(() => navigate("/dashboard"), 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg p-8 bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl z-10 relative"
      >
        <button 
          onClick={() => navigate(-1)}
          className="absolute left-6 top-8 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
        </button>

        {!submitted ? (
          <>
            <div className="flex flex-col items-center mb-10 text-center">
              <div className="p-3 bg-emerald-500/10 rounded-2xl mb-4">
                <MessageSquare className="text-emerald-500 w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight">Share Your Feedback</h1>
              <p className="text-slate-400 mt-2">How is your experience with the Fitness Tracker?</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Star Rating */}
              <div className="flex flex-col items-center gap-3">
                <span className="text-sm font-medium text-slate-400 uppercase tracking-widest">Your Rating</span>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      type="button"
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHover(star)}
                      onMouseLeave={() => setHover(0)}
                      className="focus:outline-none"
                    >
                      <Star
                        size={40}
                        className={`transition-colors duration-200 ${
                          star <= (hover || rating)
                            ? "fill-emerald-500 text-emerald-500"
                            : "text-slate-700 fill-transparent"
                        }`}
                      />
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Comment Box */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300 ml-1">Tell us more (optional)</label>
                <textarea
                  rows="4"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="What can we improve? What features do you love?"
                  className="w-full p-4 bg-slate-800/50 border border-slate-700 rounded-2xl text-white focus:ring-2 focus:ring-emerald-500 outline-none resize-none transition-all"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading || rating === 0}
                className="w-full py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg shadow-emerald-900/20 flex justify-center items-center gap-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Submit Feedback"}
                {!loading && <Send size={18} />}
              </motion.button>
            </form>
          </>
        ) : (
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="py-12 flex flex-col items-center text-center"
          >
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 size={48} className="text-emerald-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Thank You!</h2>
            <p className="text-slate-400">
              Your feedback helps us build a better experience for everyone.
            </p>
            <p className="text-slate-500 text-sm mt-8 italic">Redirecting you back...</p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default FeedbackPage;